import { useRef, useState, type ChangeEvent } from 'react';
import type { ConfirmPayload, UploadPayload } from '../api/types';
import { useProgramConfig } from '../config/useProgramConfig';
import { usePostIntake } from '../hooks/usePostIntake';
import { useTranslation } from '../i18n';
import { useIntake } from '../state/IntakeContext';
import { fileToBase64, heicToJpeg, isHeic } from '../utils/heic';
import { ExtractionReview, type ExtractionReviewResult } from './ExtractionReview';
import { ThemeToggle } from './ThemeToggle';
import './UploadScreen.css';

type Mode = 'pick' | 'processing' | 'review';

export function UploadScreen() {
  const { t } = useTranslation();
  const { setPhase } = useIntake();
  const config = useProgramConfig();
  const { send, loading: sendLoading, error } = usePostIntake();

  const [mode, setMode] = useState<Mode>('pick');
  const [extracted, setExtracted] = useState<Record<string, unknown>>({});
  const [conversionError, setConversionError] = useState<string | null>(null);

  // Explicit button + hidden input via ref keeps the file picker keyboard-accessible.
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processing = mode === 'processing' || sendLoading;

  async function onPickFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setConversionError(null);
    setMode('processing');

    try {
      const workingFile = isHeic(file) ? await heicToJpeg(file) : file;
      const base64 = await fileToBase64(workingFile);

      const payload: UploadPayload = {
        document: base64,
        mime: workingFile.type || 'application/octet-stream',
      };

      const res = await send<UploadPayload>({
        action: 'upload',
        payload,
        // Gemma 31b multimodal can take 10–30s on a complex document.
        timeoutMs: 90_000,
      });

      setExtracted(res.extras?.extracted ?? {});
      setMode('review');
    } catch (err) {
      setConversionError(err instanceof Error ? err.message : String(err));
      setMode('pick');
    }
  }

  async function onConfirm(result: ExtractionReviewResult) {
    const payload: ConfirmPayload = {
      confirmed: result.confirmed,
      rejected: result.rejected,
    };
    try {
      await send<ConfirmPayload>({ action: 'confirm', payload });
      setPhase('chat');
    } catch {
      /* surfaced via `error` */
    }
  }

  function onSkip() {
    setPhase('chat');
  }

  return (
    <main id="main" className="cf-upload-screen">
      <header className="cf-upload-header">
        <ThemeToggle />
      </header>

      <section className="cf-upload-prompt">
        <h1 className="cf-upload-title">{t('upload.prompt')}</h1>
      </section>

      {mode === 'pick' && (
        <section className="cf-upload-actions" aria-label="Upload or skip">
          <button
            type="button"
            className="cf-btn cf-btn--primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={processing}
          >
            <span aria-hidden="true">📄</span>
            <span>{t('upload.choose_file')}</span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.heic,.heif,application/pdf"
            className="cf-visually-hidden"
            onChange={onPickFile}
            disabled={processing}
            tabIndex={-1}
            aria-hidden="true"
          />
          <button
            type="button"
            className="cf-btn cf-btn--ghost"
            onClick={onSkip}
            disabled={processing}
          >
            {t('upload.skip')}
          </button>

          {conversionError && (
            <p className="cf-upload-error" role="alert">
              <span aria-hidden="true">⚠</span> {conversionError}
            </p>
          )}
        </section>
      )}

      {mode === 'processing' && (
        <section
          className="cf-upload-processing"
          role="status"
          aria-live="polite"
        >
          <span aria-hidden="true" className="cf-upload-spinner" />
          <span>{t('upload.uploading')}</span>
        </section>
      )}

      {mode === 'review' && (
        <ExtractionReview
          extracted={extracted}
          config={config}
          loading={sendLoading}
          onConfirm={onConfirm}
        />
      )}

      {mode === 'review' && error && (
        <p className="cf-upload-error" role="alert">
          <span aria-hidden="true">⚠</span> {t('chat.error_network')}
        </p>
      )}
    </main>
  );
}
